import {Exclude, Expose, instanceToPlain, plainToInstance, Transform} from "class-transformer";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../web/validation";
import {Entity, EntityIdentifier} from "./entity";
import {TimeGroup, toTimeGroup} from "./time-group";
import {getDatabaseCodeTransformer} from "./transfomers/transformers";
import {RegionEntity} from "./region";
import {Quantity} from "../types/types";
import {FreightTypeEntity} from "./freight-type";
import {Exportable} from "./exportable";
import {adjustFromTimeZone, format} from "../date/date";
import {MeterEntity} from "./meter";

export const MeterFlowVolumeValidator = t.intersection([
  t.type({
    id: t.string,
    meterId: t.string,
    timeGroup: t.string,
    dttm: t.number,
    quantity: t.number,
    actualQuantity: t.number,
    unitOfMeasure: t.string,
    regionId: t.string,
  }),
  t.partial({
    quantityOverride: t.number,
    quantityOverrideComment: t.string,
    meterName: t.string,
    freightTypeId: t.string,
    freightTypeName: t.string,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
]);

export const MeterFlowVolumeArrayValidator = t.array(MeterFlowVolumeValidator);

export type MeterFlowVolume = t.TypeOf<typeof MeterFlowVolumeValidator>;
export type MeterFlowVolumeArray = t.TypeOf<typeof MeterFlowVolumeArrayValidator>;

@Exclude()
export class MeterFlowVolumeEntity implements Entity, Exportable {
  @Expose()
  id!: string;

  @Expose()
  meterId!: string;

  @Expose()
  meterName?: string;

  @Transform(getDatabaseCodeTransformer(toTimeGroup))
  @Expose()
  timeGroup!: TimeGroup;

  @Expose()
  dttm!: number;

  @Expose()
  quantity!: number;

  @Expose()
  actualQuantity!: number;

  @Expose()
  quantityOverride?: number;

  @Expose()
  quantityOverrideComment?: string;

  @Expose()
  freightTypeId!: string;

  @Expose()
  freightTypeName?: string;

  @Expose()
  unitOfMeasure!: string;

  @Expose()
  regionId!: string;

  @Expose()
  createDttm?: number;

  @Expose()
  lastModifiedDttm?: number;

  readonly entityType = 'METER_FLOW_VOLUME';

  get displayName(): Readonly<string> {
    return this.id;
  }

  /**
   * Constructor for MeterFlowVolumeEntity.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(value: MeterFlowVolume): MeterFlowVolumeEntity {
    return plainToInstance(MeterFlowVolumeEntity, value, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<MeterFlowVolume> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(MeterFlowVolumeValidator, object);
  }

  asTypeValue(): MeterFlowVolume {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(MeterFlowVolumeValidator, object);
  }

  setRegion(value: RegionEntity | string) {
    if (typeof value === 'string') {
      this.regionId = value;
    } else {
      this.regionId = value.id;
    }
    return this;
  }

  setMeter(value: MeterEntity | string) {
    if (typeof value === 'string') {
      this.meterId = value;
    } else {
      this.meterId = value.id;
      this.meterName = value.name
    }
    return this;
  }

  setQuantity(quantity: Quantity) {
    this.quantity = quantity.number;
    this.actualQuantity = quantity.number
    this.unitOfMeasure = quantity.units;
    return this;
  }

  setFreightType(value: FreightTypeEntity | string) {
    if (typeof value === 'string') {
      this.freightTypeId = value;
    } else {
      this.freightTypeId = value.id;
      this.freightTypeName = value.name;
    }
    return this;
  }

  readonly CSV_HEADERS: string[] = [
    'Date',
    'Time Group',
    'Meter',
    'Quantity',
    'Quantity Override',
    'Unit Of Measure',
    'Freight Type',
  ];

  asCsv(): string[] {
    return [
      format(adjustFromTimeZone(this.dttm), 'yyyy-MM-dd HH:mm:ss'),
      this.timeGroup.code,
      this.meterName ?? this.meterId,
      this.actualQuantity.toString(),
      this.quantityOverride?.toString() ?? '',
      this.unitOfMeasure,
      this.freightTypeName ?? this.freightTypeId,
    ];
  }
}

export const MeterFlowVolumeImportResultsValidator = t.intersection([
  t.type({
    importedItems: t.array(MeterFlowVolumeValidator),
    missingMappings: t.array(t.string),
  }),
  t.partial({
    // partial props here
  }),
]);

export type MeterFlowVolumeImportResults = t.TypeOf<typeof MeterFlowVolumeImportResultsValidator>;

export interface MeterFlowVolumeSearchCriteria {
  fromDttm?: number;
  toDttm?: number;
  regionIds?: string[];
  freightTypeIds?: string[];
  categories?: string[];
  meterIds?: string[]
  flowMeterTypes?: string[]
  entityIds?: EntityIdentifier[];
}

export interface MeterFlowVolumeTotalSearchCriteria extends MeterFlowVolumeSearchCriteria {
  timeGroup?: string
}
